import { motion } from "framer-motion";
import { Quote } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

const CEO_IMAGE =
  "https://customer-assets.emergentagent.com/job_premium-home-decor-13/artifacts/tcs7lvj6_ChatGPT%20Image%20May%206%2C%202026%2C%2001_35_56%20AM.png";

export default function Leadership() {
  const { t, dir } = useI18n();
  const isRtl = dir === "rtl";

  return (
    <section id="leadership" className="bg-background" data-testid="leadership-section">
      <div className="container-page section-y">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, x: isRtl ? 30 : -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5 relative"
            data-testid="leadership-portrait"
          >
            <div className="relative aspect-[4/5] overflow-hidden bg-secondary">
              <img
                src={CEO_IMAGE}
                alt={t.leadership.name}
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="leadership-ceo-image"
              />
              <div className="absolute inset-0 ring-1 ring-inset ring-black/5 pointer-events-none" />
            </div>

            <div className={`hidden sm:block absolute -bottom-6 w-2/3 h-28 border border-primary pointer-events-none ${isRtl ? "-left-6" : "-right-6"}`} />
            <div className={`hidden sm:flex absolute -top-6 h-28 w-28 border border-secondary/20 items-end p-4 pointer-events-none ${isRtl ? "-right-6 justify-end" : "-left-6 justify-start"}`}>
              <span className="overline text-primary">{t.leadership.est}</span>
            </div>

            <div
              className={`absolute -bottom-10 left-6 right-6 sm:w-72 bg-secondary text-secondary-foreground p-6 shadow-xl grain ${
                isRtl ? "sm:left-12 sm:-right-4 sm:bottom-12" : "sm:left-auto sm:-right-4 sm:bottom-12"
              }`}
              data-testid="leadership-nameplate"
            >
              <div className="overline text-primary">{t.leadership.role}</div>
              <div className="mt-3 text-2xl font-bold leading-tight">{t.leadership.name}</div>
              <div className="mt-2 text-xs text-secondary-foreground/60 tracking-widest">
                {t.leadership.companyLine}
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-7 mt-20 lg:mt-0"
          >
            <span className="overline">{t.leadership.eyebrow}</span>
            <h2 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.1]">
              {t.leadership.heading1} <br />
              <span className="text-primary">{t.leadership.heading1Accent}</span>
            </h2>

            <div className="mt-10 relative">
              <Quote className={`absolute h-12 w-12 text-primary/20 ${isRtl ? "-top-2 -right-2 scale-x-[-1]" : "-top-2 -left-2"}`} />
              <blockquote
                className={`relative text-lg sm:text-xl text-foreground/85 leading-relaxed font-medium ${isRtl ? "pr-8" : "pl-8"}`}
                data-testid="leadership-quote"
              >
                {t.leadership.quote}
              </blockquote>
            </div>

            <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-px bg-border border border-border" data-testid="leadership-facts">
              {t.leadership.facts.map((f, i) => (
                <div key={i} className="bg-background px-5 py-5">
                  <div className="overline">{f.label}</div>
                  <div className="mt-1 text-sm font-semibold">{f.value}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
